/**
 * LoginPage Component - Multi-Step Registration
 * 
 * Step 1: Username & Email (required)
 * Step 2: Profile Information (optional - for personalized recommendations)
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { createUser, getUserByUsername, getRegistrationOptions, RegistrationOptions } from '../services/api';

const LoginPage: React.FC = () => {
  const [isRegister, setIsRegister] = useState(true);
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [loadingOptions, setLoadingOptions] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  // Step 1 fields
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [age, setAge] = useState('');
  const [sex, setSex] = useState('');
  const [loginUsername, setLoginUsername] = useState('');

  // Step 2 fields (profile)
  const [educationLevel, setEducationLevel] = useState('');
  const [studySpecialty, setStudySpecialty] = useState('');
  const [employmentStatus, setEmploymentStatus] = useState('');
  const [workSector, setWorkSector] = useState('');

  // Dropdown options
  const [options, setOptions] = useState<RegistrationOptions | null>(null);

  useEffect(() => {
    const loadOptions = async () => {
      try {
        const response = await getRegistrationOptions();
        setOptions(response.data);
      } catch (err) {
        console.error('Failed to load options:', err);
      } finally {
        setLoadingOptions(false);
      }
    };
    loadOptions();
  }, []);

  const handleNextStep = (e: React.FormEvent) => {
    e.preventDefault();
    if (username.trim() && email.trim() && age.trim() && sex) {
      setError('');
      setStep(2);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (isRegister) {
        const response = await createUser({
          username,
          email,
          age: age ? parseInt(age) : undefined,
          sex: sex || undefined,
          education_level: educationLevel || undefined,
          study_specialty: studySpecialty || undefined,
          employment_status: employmentStatus || undefined,
          work_sector: workSector || undefined
        });
        localStorage.setItem('userId', response.data.id.toString());
        localStorage.setItem('username', response.data.username);
      } else {
        const response = await getUserByUsername(loginUsername);
        localStorage.setItem('userId', response.data.id.toString());
        localStorage.setItem('username', response.data.username);
      }
      navigate('/dashboard');
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Échec de la connexion');
    } finally {
      setLoading(false);
    }
  };

  const handleSkipProfile = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await createUser({ 
        username, 
        email,
        age: age ? parseInt(age) : undefined,
        sex: sex || undefined
      });
      localStorage.setItem('userId', response.data.id.toString());
      localStorage.setItem('username', response.data.username);
      navigate('/dashboard');
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Échec de la création du compte');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary via-white to-secondary flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-sm">
        {/* Header */}
        <div className="mb-5">
          <div className="flex justify-center mb-2">
            <div style={{ width: '400px', overflow: 'hidden', display: 'flex', justifyContent: 'flex-start' }}>
              <img src="/logo.svg" alt="Logo" style={{ width: '600px', height: 'auto' }} />
            </div>
          </div>
          <h1 className="text-xl font-bold text-primary text-center">SkillSprint Challenge</h1>
        </div>

        {/* Register/Login Toggle */}
        <div className="mb-4 flex gap-2">
          <button
            type="button"
            onClick={() => { setIsRegister(true); setStep(1); setError(''); }}
            className={`flex-1 py-2 rounded-lg text-sm font-semibold transition ${
              isRegister ? 'bg-primary text-white' : 'bg-gray-100 text-gray-600'
            }`}
          >
            Inscription
          </button>
          <button
            type="button"
            onClick={() => { setIsRegister(false); setStep(1); setError(''); }}
            className={`flex-1 py-2 rounded-lg text-sm font-semibold transition ${
              !isRegister ? 'bg-primary text-white' : 'bg-gray-100 text-gray-600'
            }`}
          >
            Connexion
          </button>
        </div>

        {/* Step Indicator (only for registration) */}
        {isRegister && (
          <div className="flex items-center justify-center gap-2 mb-4">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
              step >= 1 ? 'bg-primary text-white' : 'bg-gray-200 text-gray-500'
            }`}>1</div>
            <div className={`w-12 h-1 rounded ${step >= 2 ? 'bg-primary' : 'bg-gray-200'}`}></div>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
              step >= 2 ? 'bg-primary text-white' : 'bg-gray-200 text-gray-500'
            }`}>2</div>
          </div>
        )}

        {/* Forms */}
        {isRegister ? (
          step === 1 ? (
            // Step 1: Basic Info
            <form onSubmit={handleNextStep} className="space-y-3">
              <div>
                <label className="block text-gray-700 text-sm font-semibold mb-1">
                  Nom d'utilisateur
                </label>
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="Votre nom d'utilisateur"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  required
                />
              </div>
              <div>
                <label className="block text-gray-700 text-sm font-semibold mb-1">
                  Email
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="votre.email@exemple.com"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  required
                />
              </div>

              <div className="flex gap-3">
                <div className="flex-1">
                  <label className="block text-gray-700 text-sm font-semibold mb-1">
                    Âge
                  </label>
                  <input
                    type="number"
                    value={age}
                    onChange={(e) => setAge(e.target.value)}
                    placeholder="Votre âge"
                    min="10"
                    max="100"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                    required
                  />
                </div>
                <div className="flex-1">
                  <label className="block text-gray-700 text-sm font-semibold mb-1">
                    Sexe
                  </label>
                  {loadingOptions ? (
                    <div className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-gray-50">
                      <span className="text-gray-400">Chargement...</span>
                    </div>
                  ) : (
                    <select
                      value={sex}
                      onChange={(e) => setSex(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-white"
                      required
                    >
                      <option value="">Sélectionnez</option>
                      {options?.sex_options.map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  )}
                </div>
              </div>

              {error && <p className="text-red-500 text-sm">{error}</p>}

              <button
                type="submit"
                className="w-full bg-primary text-white font-bold py-2.5 rounded-lg transition hover:bg-opacity-90"
              >
                Suivant
              </button>
            </form>
          ) : (
            // Step 2: Profile Info
            <form onSubmit={handleSubmit} className="space-y-3">
              <p className="text-xs text-gray-500 text-center mb-2">
                Ces informations personnalisent vos recommandations
              </p>

              {loadingOptions ? (
                <div className="text-center py-4">
                  <div className="animate-spin inline-block w-5 h-5 border-2 border-primary border-t-transparent rounded-full"></div>
                </div>
              ) : options && (
                <>
                  <SelectField
                    label="Niveau d'études"
                    value={educationLevel}
                    onChange={setEducationLevel}
                    options={options.education_levels}
                    placeholder="Sélectionnez..."
                  />
                  <SelectField
                    label="Spécialité"
                    value={studySpecialty}
                    onChange={setStudySpecialty}
                    options={options.study_specialties}
                    placeholder="Sélectionnez..."
                  />
                  <SelectField
                    label="Situation professionnelle"
                    value={employmentStatus}
                    onChange={setEmploymentStatus}
                    options={options.employment_status}
                    placeholder="Sélectionnez..."
                  />
                  {employmentStatus && !['etudiant', 'recherche_emploi', 'sans_emploi'].includes(employmentStatus) && (
                    <SelectField
                      label="Secteur d'activité"
                      value={workSector}
                      onChange={setWorkSector}
                      options={options.work_sectors}
                      placeholder="Sélectionnez..."
                    />
                  )}
                </>
              )}

              {error && <p className="text-red-500 text-sm">{error}</p>}

              <div className="flex gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setStep(1)}
                  className="flex-1 border border-gray-300 text-gray-600 font-semibold py-2 rounded-lg"
                >
                  Retour
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 bg-primary text-white font-bold py-2 rounded-lg disabled:opacity-50"
                >
                  {loading ? '...' : 'Créer'}
                </button>
              </div>

              <button
                type="button"
                onClick={handleSkipProfile}
                disabled={loading}
                className="w-full text-gray-500 text-sm py-1 hover:text-gray-700"
              >
                Passer cette étape
              </button>
            </form>
          )
        ) : (
          // Login Form
          <form onSubmit={handleSubmit} className="space-y-3">
            <div>
              <label className="block text-gray-700 text-sm font-semibold mb-1">
                Nom d'utilisateur
              </label>
              <input
                type="text"
                value={loginUsername}
                onChange={(e) => setLoginUsername(e.target.value)}
                placeholder="Entrez votre nom d'utilisateur"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                required
              />
            </div>

            {error && <p className="text-red-500 text-sm">{error}</p>}

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-primary text-white font-bold py-2.5 rounded-lg disabled:opacity-50"
            >
              {loading ? 'Connexion...' : 'Se connecter'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

// Compact select field
interface SelectFieldProps {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
  placeholder: string;
}

const SelectField: React.FC<SelectFieldProps> = ({ label, value, onChange, options, placeholder }) => (
  <div>
    <label className="block text-gray-700 text-xs font-semibold mb-1">{label}</label>
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-white"
    >
      <option value="">{placeholder}</option>
      {options.map((opt) => (
        <option key={opt.value} value={opt.value}>{opt.label}</option>
      ))}
    </select>
  </div>
);

export default LoginPage;
